import { RawDraftContentState } from 'draft-js'
import type { IReactionSummary, IReactor } from '../helpers/reactions'
import { IArcVoiceCallRecord } from './arcVoice'
import { IAtlasAIMention } from './atlasai'
import { IChannelTaskSuggestion } from './channelTaskSuggestion'
import { IHuddleRecord } from './huddles'
import { ITask } from './task'
import { IAimStatus } from './user'

/**
 * Internal team messaging: channels, conversations and DMs.
 *
 * Deliberately a separate model from the Atlas chat session/history pair.
 * `atlas_chat_history` is a question/response *turn* table — one row holds a
 * user question and Arc's answer — so it cannot represent a human-to-human
 * sequence. These types back real message rows instead. Arc participates as a
 * member (messages with `kind: Arc`), bound back to its own chat session via
 * `arcSessionId`.
 */

export enum ChannelKind {
  Channel = 'channel',
  Dm = 'dm',
  GroupDm = 'group_dm',
}

/** The kind of record a conversation is bound to, whose page embeds it (see channel.arc_scope_kind). */
export enum ArcScopeKind {
  Report = 'report',
}

export enum ChannelVisibility {
  /** Browsable and joinable by any internal user. */
  Public = 'public',
  /** Invite only, and absent from Browse results. DMs are always private. */
  Private = 'private',
}

export enum ChannelMemberRole {
  Owner = 'owner',
  Admin = 'admin',
  Member = 'member',
}

/**
 * Per-member push gating, checked alongside `mutedUntil`.
 *
 * A group offers three of these, named for the reader: `All` is Primary (every
 * message in every thread), `Engaged` is Engagement (threads you have posted in,
 * plus @-mentions) and `None` is Mute. `Engaged` is a group's default and is only
 * meaningful on a group's own row — see `isSilencedInGroupConversation`.
 */
export enum ChannelNotificationLevel {
  All = 'all',
  Engaged = 'engaged',
  Mentions = 'mentions',
  None = 'none',
}

export enum ChannelMessageKind {
  /** Posted by a person. */
  Message = 'message',
  /** Generated notice: joins, leaves, topic changes, archive. */
  System = 'system',
  /** Posted by Arc. */
  Arc = 'arc',
}

export interface IChannel {
  id: string
  /** Kebab-case handle, unique among non-deleted channels. Null for DMs. */
  slug: string | null
  /** Display name. Null for DMs, which are titled from their participants. */
  name: string | null
  /** The glyph a group is shown with. Null falls back to the default. */
  emoji: string | null
  topic: string | null
  description: string | null
  /**
   * Arc's account of what this conversation is about — the description behind a
   * title that is only three to five words. Null until the conversation has run
   * long enough to be worth describing; see nameConversation.ts.
   */
  aiSummary: string | null
  visibility: ChannelVisibility
  kind: ChannelKind
  /**
   * The named channel this conversation belongs to. Null on a channel itself and
   * on a DM-keyed conversation, which is grouped by its participants instead.
   */
  parentChannelId: string | null
  createdByUserId: string
  /** Model Arc runs on here. Null follows the current default. */
  arcModel: string | null
  /**
   * Whether Arc may answer a message here it was never @-mentioned in. Off
   * until an owner or admin opts the channel in; only meaningful for
   * `ChannelKind.Channel`. A conversation inside a channel follows the channel's.
   */
  arcAutoReply: boolean
  /** Set when archived: read-only, hidden from Browse, membership preserved. */
  archivedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface IChannelMember {
  id: string
  channelId: string
  userId: string
  role: ChannelMemberRole
  joinedAt: string
  /** Non-null means the member left; fanout and permission checks skip them. */
  leftAt: string | null
  lastReadMessageId: string | null
  lastReadAt: string | null
  notificationLevel: ChannelNotificationLevel
  mutedUntil: string | null
  isPinned: boolean
  /** Joined for display; absent on write paths. */
  name?: string
  /** The compact form, for conversation titles. See `shortDisplayName`. */
  shortName?: string
  avatar?: string | null
  /**
   * The member's personal status, expiry already applied — null when they have
   * none. Joined for display like `name`, so absent on write paths.
   *
   * Deliberately on the member payload and NOT on `IChannelMessage`: a message
   * is a historical record, and stamping a mutable status onto all 50 rows of a
   * page would duplicate it per row and go stale the moment it changed.
   */
  aimStatus?: IAimStatus | null
}

export interface IChannelMembershipEvent {
  channelId: string
  actorUserId: string
  actor: { id: string; name: string; avatar: string | null }
  dmKey: string | null
}

/** Where an attachment's bytes live. See the S3 storage migration for why both. */
export enum ChannelAttachmentStorage {
  /** Rows predating the S3 move. Addressed by `boxFileId`, previewed by BoxViewer. */
  Box = 'box',
  /** Everything uploaded since. Addressed by an object key the client never sees. */
  S3 = 's3',
}

/**
 * Which copy GetChannelAttachmentLink returns: the file as sent, or the downscaled copy
 * the composer uploads beside an image too large to render inline.
 */
export enum ChannelAttachmentLinkVariant {
  Original = 'original',
  Preview = 'preview',
}

/** Where a voice memo's transcript stands. It is written after the message lands. */
export enum VoiceMemoTranscriptionStatus {
  Pending = 'pending',
  Completed = 'completed',
  Failed = 'failed',
}

/** What makes an audio attachment a recorded voice memo rather than a file. */
export interface IChannelVoiceMemo {
  durationMs: number
  transcriptionStatus: VoiceMemoTranscriptionStatus
  /** Empty string when nothing was heard; null until transcription completes. */
  transcript: string | null
  /** User-facing reason, only when `transcriptionStatus` is `Failed`. */
  transcriptionError: string | null
  /** When the latest attempt started — what decides a pending transcript is stuck. */
  transcriptionStartedAt: string
}

export interface IChannelMessageAttachment {
  id: string
  messageId: string
  channelId: string
  storage: ChannelAttachmentStorage
  /** Null for S3-backed rows, which have no Box file. */
  boxFileId: string | null
  name: string
  mimeType: string | null
  sizeBytes: number | null
  uploadedByUserId: string
  /** A downscaled copy exists, so an image over the inline cap can still render inline. */
  hasPreview: boolean
  /** Null for an ordinary file. */
  voiceMemo?: IChannelVoiceMemo | null
}

/**
 * Channel-flavoured aliases for the shared reaction shapes. The vocabulary lives
 * in common/helpers/reactions — task comments use the same one.
 */
export type IChannelMessageReactor = IReactor
export type IChannelMessageReactionSummary = IReactionSummary

/**
 * Outcome of the last unfurl attempt for a URL.
 *
 * Every non-`Ok` value is a reason there is nothing to render, kept apart rather
 * than collapsed to one "failed" so the TTL can treat a site that will never have
 * metadata differently from one that timed out today.
 */
export enum LinkPreviewStatus {
  /** Something worth showing — at least a title, description or image. */
  Ok = 'ok',
  /** Fetched and parsed fine, but the page declares nothing renderable. */
  NoMetadata = 'no_metadata',
  /** A PDF, an image, a direct download — nothing to scrape. */
  NonHtml = 'non_html',
  /** 404, timeout, refused, or a host the SSRF guard would not let us reach. */
  Failed = 'failed',
}

/**
 * The unfurl card for the first link in a message.
 *
 * Only ever present when there is something to draw: the server withholds it for
 * every non-`Ok` status, so a client never has to decide whether a preview is
 * worth rendering, and a blocked or broken link simply leaves the message as it
 * was. Absent — rather than empty — while the fetch is still in flight; it arrives
 * later on a `CHANNEL_MESSAGE_UPDATED` frame.
 */
export interface IChannelMessageLinkPreview {
  /** The link this describes, normalized. What the card navigates to. */
  url: string
  title: string | null
  description: string | null
  imageUrl: string | null
  faviconUrl: string | null
  siteName: string | null
}

/**
 * The quoted line a reply shows above its own body, so what is being answered
 * is visible without chasing a link.
 */
export interface IChannelMessageReplyPreview {
  id: string
  senderName: string | null
  /** Plain-text excerpt of the parent, already truncated for one quote line. */
  excerpt: string
  /** The parent was deleted after this reply was written; `excerpt` is empty. */
  isDeleted: boolean
  /** Parent's rich body, so the quote can render chips the way the original does. */
  contentState?: RawDraftContentState | null
  mentions?: IAtlasAIMention[]
  /** Decides how the body is parsed — an Arc parent's `excerpt` is markdown. */
  kind?: ChannelMessageKind
}

export interface IChannelMessage {
  id: string
  channelId: string
  senderUserId: string
  kind: ChannelMessageKind
  /** Rich-text body; the source of truth for rendering. */
  contentState: RawDraftContentState | null
  /** Plain-text mirror, used for search, previews and notification text. */
  content: string
  mentions: IAtlasAIMention[]
  /** Set on messages that invoked or came from Arc. */
  /** The sender's own id for this send, echoed back so a client can match its optimistic row. */
  clientMessageId?: string | null
  arcSessionId: string | null
  /**
   * The `atlas_chat_history` turn that produced this message. Null for anything
   * Arc did not write. Distinct from `arcSessionId`, which spans the whole
   * conversation — this names one run, which is what feedback attaches to.
   */
  arcTurnId: string | null
  /**
   * The message this one answers. Null on an ordinary message, and null once the
   * parent is hard-deleted — the reply outlives what it answered.
   */
  replyTo?: IChannelMessageReplyPreview | null
  /** Text it answers that isn't a message, e.g. the call lines an Arc Live note replies to. */
  quote?: string | null
  /** The length and transcript of an Arc Voice call, on the System row it leaves. */
  voiceCall?: IArcVoiceCallRecord | null
  /** The call this message was said on: folded under that call's row, not shown inline. */
  voiceCallMessageId?: string | null
  /** Who was in a huddle and for how long, on the System row it leaves. */
  huddle?: IHuddleRecord | null
  /** Channel-wide pin. Null means not pinned. */
  pinnedAt: string | null
  pinnedByUserId: string | null
  editedAt: string | null
  createdAt: string
  updatedAt: string
  attachments?: IChannelMessageAttachment[]
  reactions?: IChannelMessageReactionSummary[]
  /**
   * Unfurl for the first link in the body, or `null` when there is nothing to
   * draw — the fetch is resolved off the send path, so it is null until that
   * lands and null for good when the link had nothing renderable behind it.
   *
   * Explicitly nullable rather than merely optional: the update socket frame is
   * merged into the message already on screen, so only a present key can clear a
   * card that an edit has invalidated. See `serializeLinkPreview`.
   */
  linkPreview?: IChannelMessageLinkPreview | null
  /**
   * The create/open-task prompt under this message. Nullable for the same reason
   * as `linkPreview`: a dismissal arrives as an update frame and must clear it.
   */
  taskSuggestion?: IChannelTaskSuggestion | null
  /** Joined sender display fields. */
  senderName?: string
  /**
   * The sender's compact name — a nickname whole, otherwise their first name.
   * Resolved on the server because `firstNameOf` cannot tell the two apart, and a
   * multi-word nickname cut at the space ("Big Mike" → "Big") defeats the field.
   */
  senderShortName?: string
  senderAvatar?: string | null
  /** Who asked Arc to post this, as a short name — shown in Arc's badge. Null otherwise. */
  sentOnBehalfOfShortName?: string | null
  /** Set when a personal access token sent it; the client shows an Automated chip. */
  accessTokenId?: string | null
}

/**
 * A channel as it appears in the sidebar. `unreadCount` and `mentionCount` are
 * derived per request from the caller's `lastReadAt` cursor — never stored, so
 * one member reading cannot clear another's badge (the bug that
 * `atlas_chat_session.isUnread` has by design).
 */
export interface IChannelListItem extends IChannel {
  unreadCount: number
  mentionCount: number
  isPinned: boolean
  notificationLevel: ChannelNotificationLevel
  mutedUntil: string | null
  memberCount: number
  lastMessageAt: string | null
  lastMessagePreview: string | null
  /** Participants, for rendering DM titles and avatar groups. */
  members?: Array<Pick<IChannelMember, 'userId' | 'name' | 'shortName' | 'avatar' | 'aimStatus'>>
}

/**
 * Every named conversation a participant set holds, collapsed to one sidebar row.
 *
 * Arc's migrated chat sessions are these: one person has several hundred, and
 * listing them flat would be several hundred identically-titled rows. The group
 * is the entry you see; the conversations inside it are fetched on expand.
 *
 * Keyed by `dm_key` — the participant fingerprint — rather than by a channel id,
 * because the group *is* the participant set, not any one conversation in it.
 */
/** One message in a share link's preview — enough to recognise the conversation. */
export interface IChannelSharePreviewMessage {
  id: string
  senderName: string
  /** Rendered with Arc's label rather than as a teammate's message. */
  isArc: boolean
  content: string
  createdAt: string
}

/**
 * What someone opening a share link is shown before deciding to join.
 *
 * A preview rather than the conversation itself: the caller is not a member yet,
 * so this is the one payload that deliberately returns channel content to a
 * non-member — capped, and only ever for a link they were given.
 */
export interface IChannelShareLanding {
  channelId: string
  /** Viewer-relative: a DM with no name of its own falls back to its participants. */
  name: string
  kind: ChannelKind
  slug: string | null
  memberCount: number
  /** Who created the link. Null if that account is gone. */
  sharer: { id: string; name: string; avatar: string | null } | null
  messages: IChannelSharePreviewMessage[]
  /**
   * The caller is already in this conversation, so there is nothing to preview or
   * join — the client sends them straight in.
   */
  isAlreadyMember: boolean
}

export interface IConversationGroup {
  dmKey: string
  /** Everyone but the viewer, already resolved for the row label and avatars. */
  members: Array<Pick<IChannelMember, 'userId' | 'name' | 'shortName' | 'avatar' | 'aimStatus'>>
  /**
   * The set is Arc alone — a 1:1 with the bot, not a conversation with people.
   *
   * Decided on the server because that is where the roles already are; the client
   * has names and avatars but nothing that says which user is the bot. The sidebar
   * splits on this so Arc gets its own section: one account holds several hundred
   * Arc conversations and a handful with each teammate, so leaving them in one
   * list buries the people under the bot.
   *
   * A group DM that merely *includes* Arc is not this — that is still a
   * conversation with people, and belongs under them.
   */
  isArc: boolean
  conversationCount: number
  lastMessageAt: string | null
  /** Summed across the group, so the row reports the whole set at a glance. */
  unreadCount: number
  /**
   * Also summed. Kept separate from `unreadCount` because the two are different
   * levels of urgency — "someone needs me" outranks "something happened" — and
   * the row renders whichever applies rather than one total.
   */
  mentionCount: number
}

/**
 * Scheduled runs of one Arc automation, collapsed to a sidebar row.
 *
 * Grouped by automation rather than listed by run: runs accumulate on a schedule
 * forever, so a flat list has no ceiling while the automation list does.
 */
export interface IScheduledRunGroup {
  automationId: string
  name: string
  runCount: number
  lastRunAt: string | null
  unreadCount: number
  mentionCount: number
}

/** One named conversation inside a group. */
export interface IConversationListItem {
  id: string
  name: string
  /**
   * Who is in it, for the avatar stack — the same shape and the same purpose as
   * the sidebar's, so a thread is identified by its participants in both places.
   */
  members: Array<Pick<IChannelMember, 'userId' | 'name' | 'shortName' | 'avatar' | 'aimStatus'>>
  lastMessageAt: string | null
  lastMessagePreview: string | null
  /** Who spoke last, so the avatar stack can lead with them as the sidebar's does. */
  lastMessageSenderId: string | null
  /**
   * One line on what the conversation was about, which the homepages show in
   * place of the preview — a preview is the *first* thing someone said, and for
   * a conversation that ran all afternoon that is its least useful line.
   *
   * Null until it has been generated (see `summarizeConversation`), so anything
   * predating summaries falls back to the preview rather than rendering empty.
   */
  summary: string | null
  unreadCount: number
  mentionCount: number
  /** Whether anyone other than the viewer (and Arc) is in it. */
  isShared: boolean
  /**
   * Whether the viewer may rename it — the server's `canRenameChannel`, decided
   * once and carried, so the Rename the row offers and the rename `Update`
   * accepts can never disagree.
   *
   * False for the canonical DM (`name IS NULL`): it is titled from whoever is in
   * it rather than from a stored name, and it is the row `resolveDmChannel` looks
   * up *by* that null name.
   */
  canRename: boolean
  /** Per-viewer sidebar pin, same `channel_member` flag as top-level channels/DMs. */
  isPinned: boolean
  notificationLevel: ChannelNotificationLevel
  mutedUntil: string | null
}

/** An existing conversation with a draft's recipients, offered as a card to carry on. */
export interface IDmConversationOption extends Pick<
  IConversationListItem,
  'id' | 'name' | 'members' | 'lastMessageAt' | 'lastMessagePreview' | 'lastMessageSenderId'
> {
  messageCount: number
}

/**
 * A conversation as the sidebar's Conversations section lists it — the flat,
 * recency-ordered view across channels, people and group chats.
 *
 * The section deliberately spans every kind, because that is the question it
 * answers: "what have I been talking about lately". Which grouping a row belongs
 * to is carried so a click can route, not so the list can be split.
 */
export interface IRecentConversationItem {
  id: string
  /** Already resolved viewer-relative — a DM's title is whoever else is in it. */
  name: string
  kind: ChannelKind
  /** Set when this conversation lives inside a channel. */
  parentChannelId: string | null
  /** The channel's display name, so a row can say where it came from. */
  parentChannelName: string | null
  /** Set when this conversation is grouped under a participant set instead. */
  dmKey: string | null
  /** Everyone but the viewer for the row's avatar bubbles; the viewer alone in a note to self. */
  members: Array<Pick<IChannelMember, 'userId' | 'name' | 'shortName' | 'avatar' | 'aimStatus'>>
  lastMessageAt: string | null
  lastMessagePreview: string | null
  /**
   * Who wrote the previewed message.
   *
   * An id, not a name: `members` above already carries the display name of
   * everyone but the viewer, so the row resolves it for free — and an id that
   * matches none of them is the viewer's own, which is how a row knows to say
   * "You" rather than naming you to yourself.
   */
  lastMessageSenderId: string | null
  unreadCount: number
  mentionCount: number
  /**
   * Whether the viewer has posted in this conversation. Only meaningful inside a
   * channel, where everyone is a member of every conversation by default and so
   * membership says nothing about whether this one is theirs — see
   * `getUnreadIndicator`. Always false for a DM.
   */
  hasParticipated: boolean
  /**
   * The viewer's settings on the group that holds this thread — the level that
   * decides whether it alerts. Null for anything that is not a group's thread.
   */
  groupSettings: Pick<IChannelListItem, 'notificationLevel' | 'mutedUntil'> | null
  isPinned: boolean
  /** The server's own rename verdict, so a control can never offer a refused write. */
  canRename: boolean
  /** A 1:1 with the bot. Badged as a dot rather than a number — see getUnreadIndicator. */
  isArc: boolean
  /** The viewer's own volume for it — what the row's Mute toggles. */
  notificationLevel: ChannelNotificationLevel
  mutedUntil: string | null
  /** Archived by the viewer; only the Archived filter lists these. Absent from an older server. */
  isArchived?: boolean
  /** Unmuted on its own while its group is muted, so it notifies anyway. */
  overridesGroupMute?: boolean
  /**
   * When the client last saw this row renamed, for the row's highlight. Set by
   * the reducer from a `CHANNEL_UPDATED` broadcast and never sent by the server —
   * a rename is only worth announcing to someone already looking at the list.
   */
  renamedAt?: number
}

/** How AIM search orders message results. */
export enum SearchOrder {
  Relevance = 'relevance',
  Newest = 'newest',
}

/** Shortest text message search accepts; conversation-name search takes any length. */
export const MESSAGE_SEARCH_MIN_QUERY_LENGTH = 2

/** A conversation whose name matched an AIM search: a thread, a group, or a DM by person. */
export interface IConversationSearchResult {
  channelId: string
  kind: ChannelKind
  name: string
  /** The group a thread belongs to; null for groups and DMs. */
  groupName: string | null
  isGroup: boolean
  lastMessageAt: string | null
  lastMessagePreview: string | null
  lastMessageSenderId: string | null
  /** Everyone but the viewer (the viewer alone in a note to self), capped — the avatar stack. */
  members: Array<Pick<IChannelMember, 'userId' | 'name' | 'shortName' | 'avatar' | 'aimStatus'>>
  memberCount: number
  isMuted: boolean
  isArchived: boolean
}

/**
 * An unread conversation as the nav badge weighs it, whatever the Threads filter
 * loaded. Unarchived and unmuted only: those are the rows the badge counts.
 */
export interface IUnreadConversationSummary {
  id: string
  unreadCount: number
  mentionCount: number
  isArc: boolean
  isChannelConversation: boolean
  hasParticipated: boolean
  /** Primary in the group, or unmuted on its own under a muted group. */
  hearsEveryMessage: boolean
}

/** How many more threads each "Show more threads" adds. */
export const RECENT_CONVERSATIONS_PAGE = 10

/**
 * How many read conversations the Conversations section shows when nothing is
 * unread. Everything unread is always listed, however much that is — the section
 * is the only place unread surfaces now, so truncating it would hide mail.
 */
export const RECENT_CONVERSATIONS_FLOOR = 10

/** How many conversations a homepage shows before "Show more" grows the window. */
export const AIM_HOME_CONVERSATION_LIMIT = 20

/**
 * What a sidebar pin can point at.
 *
 * Pinning is system-wide now: the Pinned section holds whichever of these the
 * viewer wanted kept at the top, mixed together, rather than conversations
 * alone. See the `aim_pin` table.
 */
export enum AimPinTargetType {
  Conversation = 'conversation',
  Channel = 'channel',
  Person = 'person',
}

/** One pinned entry, resolved enough for the sidebar to render it. */
export interface IAimPin {
  targetType: AimPinTargetType
  targetId: string
  /** Viewer-relative, the same title the row would carry in its own section. */
  name: string
  /** Set for a conversation living inside a channel, for the row's chip. */
  parentChannelName: string | null
  /** Everyone but the viewer — the avatar stack, empty for a channel. */
  members: Array<Pick<IChannelMember, 'userId' | 'name' | 'shortName' | 'avatar' | 'aimStatus'>>
  unreadCount: number
  mentionCount: number
  lastMessageAt: string | null
  lastMessagePreview: string | null
}

/** What a shared notepad is attached to. See the `aim_note` table. */
export enum AimNoteScopeType {
  /** Keyed by the participant pair, so both people share one note. */
  Person = 'person',
  Channel = 'channel',
}

/** The shared notepad on a homepage. */
export interface IAimNote {
  /** Plain text — the note's searchable mirror, and all there was before formatting. */
  content: string
  /** The formatted document. Null for a note written before the toolbar existed. */
  contentState?: RawDraftContentState | null
  updatedAt: string | null
  /** Who last wrote to it, so the page can say whose edit you are reading. */
  updatedByName: string | null
}

/** One teammate in the sidebar's People directory. */
export interface IPersonDirectoryEntry {
  userId: string
  /** The viewer themselves — a conversation with you is a scratchpad. */
  isSelf: boolean
  name: string
  shortName: string
  avatar: string | null
  aimStatus: IAimStatus | null
  /** The participant fingerprint for a 1:1 with them, so a row can open it. */
  dmKey: string
  /** Pinned to the top of the sidebar — see AimPinTargetType. */
  isPinned: boolean
  /** Conversations the viewer holds with them, 1:1 and group alike. */
  conversationCount: number
  lastMessageAt: string | null
}

/**
 * One file shared in a conversation, as every file surface renders it.
 *
 * Shared rather than declared on the client because two services now build it —
 * `ListFiles` for one conversation's Files tab and `loadFilesForChannels` for a
 * homepage — and a second copy of the shape is a second thing to keep in step.
 * `storageKey` is deliberately absent: it is the S3 object key, and the client
 * addresses a file by attachment id instead.
 */
export interface IChannelFile {
  id: string
  messageId: string
  channelId: string
  storage: ChannelAttachmentStorage
  /** Null for S3-backed files. */
  boxFileId: string | null
  name: string
  mimeType: string | null
  sizeBytes: number | null
  uploadedByUserId: string
  uploadedByName: string | null
  /** Nickname whole, else first name — see `shortDisplayName` on the server. */
  uploadedByShortName?: string | null
  createdAt: string
}

/**
 * Everything a channel's homepage renders. One payload rather than four calls:
 * the page shows all four blocks at once, and four round trips to draw one
 * screen is the N+1 of page loads.
 */
export interface IChannelHome {
  channel: IChannelListItem
  conversations: IConversationListItem[]
  conversationTotal: number
  /** More conversations than the page asked for — see AIM_HOME_CONVERSATION_LIMIT. */
  conversationsHaveMore: boolean
  tasks: ITask[]
  pinnedMessages: IChannelMessage[]
  files: IChannelFile[]
  /** The shared notepad for this channel. */
  note: IAimNote
}

/**
 * Arc's homepage.
 *
 * Deliberately not the person homepage with a bot id: "tasks you share" and
 * "pinned between us" are questions about a colleague, and Arc is not one. What
 * it holds instead is its conversations and the automations running on a
 * schedule — which is also why Scheduled runs stopped needing a sidebar section
 * of its own.
 */
export interface IArcHome {
  /** The participant fingerprint for a 1:1 with Arc, so a new chat can be started. */
  dmKey: string
  arcUserId: string
  conversations: IConversationListItem[]
  conversationTotal: number
  conversationsHaveMore: boolean
  /** Automations with runs, newest first. */
  scheduledRuns: IScheduledRunGroup[]
  /** Outstanding tasks Arc created while working in these conversations. */
  tasks: ITask[]
  files: IChannelFile[]
  note: IAimNote
}

/** The same blocks for a person, resolved relative to the viewer. */
export interface IPersonHome {
  person: IPersonDirectoryEntry
  /** Every conversation with them — 1:1s and group chats, never threads in a named channel. */
  conversations: IConversationListItem[]
  conversationTotal: number
  conversationsHaveMore: boolean
  /** Tasks the viewer and this person are both collaborators on. */
  tasks: ITask[]
  pinnedMessages: IChannelMessage[]
  /** Everything shared across those conversations — a smaller block than the rest. */
  files: IChannelFile[]
  /** The notepad the two of you share. */
  note: IAimNote
}

export interface IChannelTypingUser {
  userId: string
  name: string
  /** What the "X is typing" line renders. See `shortDisplayName`. */
  shortName?: string
  avatar: string | null
  startedAt: number
}

export interface IPresenceState {
  userId: string
  isOnline: boolean
  lastSeenAt: string | null
}

/** Max characters accepted in a single message body (plain-text mirror). */
export const CHANNEL_MESSAGE_MAX_LENGTH = 12_000

/** Default page size for the conversation timeline. */
export const CHANNEL_MESSAGES_PAGE_SIZE = 50

/**
 * Lifecycle of a message the sender asked to post later.
 *
 * `Sending` is the claim: the job (or a send-now) owns the row while it posts, which is
 * what keeps edit and cancel from racing a delivery already under way.
 */
export enum ScheduledChannelMessageStatus {
  Pending = 'pending',
  Sending = 'sending',
  Sent = 'sent',
  Cancelled = 'cancelled',
  Failed = 'failed',
}

/** Not yet settled — what the sender's Scheduled list shows, failed ones included. */
export const OUTSTANDING_SCHEDULED_MESSAGE_STATUSES: readonly ScheduledChannelMessageStatus[] = [
  ScheduledChannelMessageStatus.Pending,
  ScheduledChannelMessageStatus.Sending,
  ScheduledChannelMessageStatus.Failed,
]

/** An uploaded file a message points at by its storage key, as SendMessage accepts it. */
export interface IChannelAttachmentInput {
  storageKey: string
  /** The downscaled copy of an image over the inline cap, uploaded beside it. */
  previewStorageKey?: string
  name: string
  mimeType?: string
  sizeBytes?: number
}

/** A message waiting to be posted, as only its sender ever sees it. */
export interface IScheduledChannelMessage {
  id: string
  /**
   * Null for a first message to people, until sending creates its conversation; otherwise
   * null only once the conversation is gone, when the row fails rather than sends.
   */
  channelId: string | null
  /** Who it goes to — the group, or the people — then the conversation's own name if it has one. */
  channelLabel: string
  /** Everyone who will see it besides the sender, for the avatar stack. */
  members: Array<{ userId: string; name: string }>
  contentState: RawDraftContentState | null
  content: string
  attachments: IChannelAttachmentInput[]
  /** The message this answers; `isDeleted` once it is gone, when it posts as a plain message. */
  replyTo: IChannelMessageReplyPreview | null
  sendAt: string
  status: ScheduledChannelMessageStatus
  /** Why the last attempt failed, in words for the sender. */
  failureReason: string | null
  sentMessageId: string | null
  createdAt: string
  updatedAt: string
}
